/**
 * CalendarPageKPICards Component
 * 
 * Purpose:
 * KPI cards section component that displays key performance indicators (nights booked, occupancy,
 * payout) for the selected month. On mobile, shows a status notice if metrics fail to load.
 * Uses KPICardRow component for rendering the cards.
 * 
 * High-level Workflow:
 * 1. Receives calendar metrics, loading/error states, and KPI cards array as props.
 * 2. On mobile, displays status notice if calendarMetricsError exists.
 * 3. Renders KPICardRow with kpiCards array.
 * 4. Applies mobile/desktop styling based on isMobile prop.
 * 
 * Assumptions & Invariants:
 * - KPI cards data: kpiCards array is pre-computed in CalendarPage from calendarMetrics or
 *   fallback calculations. Component receives the final array and renders it.
 * - Mobile status notice: On mobile, if calendarMetricsError exists, shows a trust-grade status
 *   notice explaining that metrics are temporarily unavailable but calendar/bookings are still
 *   visible. Includes retry button that reloads the page.
 * - KPICardRow: Uses shared KPICardRow component which handles 2-column grid layout on mobile
 *   and appropriate layout on desktop.
 * - Styling: Container styling is responsive (getKPIContainerStyle) based on isMobile prop.
 * 
 * Dependencies:
 * - Internal:
 *   - components/shared/KPICardRow.jsx: KPI cards row display component
 * - External:
 *   - react: None (functional component, no hooks)
 * 
 * Version: 1.0.0
 * 
 * Change Log:
 * - 2026-01-26: Initial extraction from CalendarPage.jsx (Phase 5 refactoring)
 */

import KPICardRow from '../shared/KPICardRow.jsx';

/**
 * KPI cards section component
 * 
 * @param {boolean} isMobile - Whether mobile layout is active
 * @param {boolean} calendarMetricsError - Error state for calendar metrics fetch
 * @param {Array} kpiCards - Array of KPI card objects to display
 * @param {Object} styles - Styles object from CalendarPage
 */
export default function CalendarPageKPICards({
  isMobile,
  calendarMetricsError,
  kpiCards,
  styles,
}) {
  return (
    <div style={styles.getKPIContainerStyle(isMobile)} data-calendar-kpi-mobile={isMobile ? 'true' : undefined}>
      {/* BEYOND WORLD-CLASS: Trust-grade status notice for calendar metrics */}
      {isMobile && calendarMetricsError && (
        <div style={styles.statusNotice} data-status-notice="metrics">
          <div style={styles.statusNoticeHeader}>
            <span style={styles.statusNoticeIcon}>⚠️</span>
            <span style={styles.statusNoticeTitle}>Metrics temporarily unavailable</span>
          </div>
          <div style={styles.statusNoticeBody}>
            <p style={styles.statusNoticeText}>
              Revenue and occupancy metrics couldn't be loaded. Your calendar and bookings are still visible.
            </p>
            <button
              onClick={() => window.location.reload()}
              style={styles.statusNoticeAction}
              aria-label="Retry loading metrics"
            >
              Retry
            </button>
          </div>
        </div>
      )}
      {/* PHASE 2: On mobile, show all KPIs in 2-column grid (no expand/collapse) */}
      <KPICardRow kpis={kpiCards} isMobile={isMobile} />
    </div>
  );
}
